import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, CheckCircle2 } from "lucide-react";
import { SITE } from "../data/site";

const BENEFITS = [
  "UCAS-recognised credits",
  "Industry mentorship from working choreographers",
  "Live performance placements across the UK",
  "Pathway into teaching, production and creative direction",
];

export const Pathway = () => {
  return (
    <section
      id="pathway"
      data-testid="pathway-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <img src={SITE.pathwayBg} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <span className="h-px w-10 bg-brand-yellow" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">07 · BA Degree Pathway</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-black bg-brand-yellow px-3 py-1 rounded-full glow-yellow">Coming Soon</span>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-7xl text-white tracking-tighter leading-[0.9]"
          >
            Dance can be
            <br />
            <span className="italic text-brand-yellow text-glow-yellow">your degree.</span>
          </motion.h2>

          <p className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed">
            In partnership with validated UK universities, USDD students can
            progress from Level 2 right through to a full BA (Hons) — without
            ever leaving the studio.
          </p>

          <ul className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl">
            {BENEFITS.map((b, i) => (
              <motion.li
                key={b}
                data-testid={`pathway-benefit-${i}`}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-start gap-3 text-zinc-200"
              >
                <CheckCircle2 className="text-brand-yellow flex-shrink-0 mt-0.5" size={20} />
                <span className="text-sm leading-snug">{b}</span>
              </motion.li>
            ))}
          </ul>

          <Link
            to="/ba-pathway"
            data-testid="pathway-cta-btn"
            className="mt-10 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-105 transition-all glow-yellow"
          >
            Explore BA Pathway
            <ArrowRight size={18} />
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="relative p-8 rounded-3xl bg-gradient-to-br from-brand-purple/40 via-brand-purpleDeep/40 to-black border border-brand-purpleLight/30 backdrop-blur-xl">
            <GraduationCap className="text-brand-yellow mb-6" size={40} />
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow">Progression Map</div>
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-brand-yellow bg-brand-yellow/10 border border-brand-yellow/40 px-2 py-0.5 rounded-full">Soon</span>
            </div>
            <div className="space-y-4">
              {[
                { step: "L2", label: "Foundation Diploma" },
                { step: "L3", label: "Extended Diploma" },
                { step: "L4", label: "HND" },
                { step: "L6", label: "BA (Hons) Top-up" },
              ].map((s, i) => (
                <div key={s.step} className="flex items-center gap-4 group">
                  <div className="font-display font-black text-2xl text-brand-yellow w-12">{s.step}</div>
                  <div className="flex-1 border-t border-white/10" />
                  <div className="text-sm font-bold text-white uppercase tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-white/10 text-xs uppercase tracking-[0.3em] text-zinc-400">
              ~4 years · Full pathway
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
